/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "../components/atoms/Button";
import Pagination from "../components/molecules/Pagination";
import CustomersTable from "../components/organisms/CustomersTable";
import AppLayout from "../components/templates/AppLayout";
import CustomerCalling from "./CustomerCalling";
import CustomersService, {
  CUSTOMER_SORTS,
  Customer,
  CustomerPermissions,
  CustomerSort,
  CustomersMeta,
  NO_PERMISSIONS,
} from "../services/CustomersService";

type Order = "asc" | "desc";

type ListQuery = {
  page: number;
  perPage: number;
  sort: CustomerSort;
  order: Order;
  search: string;
};

const PER_PAGE_OPTIONS = [25, 50, 100];

/** The old page listed the newest customers first. */
const DEFAULT_QUERY: ListQuery = {
  page: 1,
  perPage: 50,
  sort: "user_id",
  order: "desc",
  search: "",
};

/** Columns where the newest or highest value is the useful first view. */
const DESCENDING_FIRST: CustomerSort[] = ["user_id", "date_came_in"];

/**
 * The order a column opens in, and so the order a URL without `order` means.
 * Names and statuses read A to Z, as the old page's ?sort= links did.
 */
function defaultOrder(sort: CustomerSort): Order {
  return DESCENDING_FIRST.includes(sort) ? "desc" : "asc";
}

/** Server error text is English and technical; never shown as-is. */
const LOAD_ERROR = "No se pudo cargar la lista de pacientes.";
const SESSION_EXPIRED =
  "Tu sesión expiró. Recarga la página para seguir viendo a tus pacientes.";

const EMPTY_META: CustomersMeta = {
  page: 1,
  per_page: DEFAULT_QUERY.perPage,
  total: 0,
  total_pages: 0,
};

/**
 * The home page's card and the sign-in form's fields, via the app-* classes
 * in tailwind.css: Bootstrap's !important .bg-white/.border/.shadow-sm would
 * otherwise beat the dark: variants on this legacy page.
 */
const cardClasses = "app-card overflow-hidden";
const inputClasses = "app-field block px-3 py-2 text-sm";

const labelClasses =
  "mb-1.5 block text-sm font-medium text-gray-900 dark:text-gray-200";

/**
 * The address bar is the source of truth, so a link, a reload and the Back
 * button all reopen the same view. A value the API would refuse falls back to
 * the default rather than turning the page into a 400.
 */
function readQuery(search: string): ListQuery {
  const params = new URLSearchParams(search);
  const page = Number(params.get("page"));
  const perPage = Number(params.get("per_page"));
  const requestedSort = params.get("sort") as CustomerSort | null;
  const sort =
    requestedSort && CUSTOMER_SORTS.includes(requestedSort)
      ? requestedSort
      : DEFAULT_QUERY.sort;
  const order = params.get("order");

  return {
    // isSafeInteger: ?page=1e20 parses as an integer the API refuses.
    page: Number.isSafeInteger(page) && page >= 1 ? page : DEFAULT_QUERY.page,
    perPage: PER_PAGE_OPTIONS.includes(perPage)
      ? perPage
      : DEFAULT_QUERY.perPage,
    sort,
    order: order === "asc" || order === "desc" ? order : defaultOrder(sort),
    search: (params.get("q") ?? "").trim(),
  };
}

/** Only what differs from the default, so the plain page keeps a plain URL. */
function writeQuery(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.page !== DEFAULT_QUERY.page) params.set("page", String(query.page));
  if (query.perPage !== DEFAULT_QUERY.perPage) {
    params.set("per_page", String(query.perPage));
  }
  if (query.sort !== DEFAULT_QUERY.sort) params.set("sort", query.sort);
  if (query.order !== defaultOrder(query.sort)) {
    params.set("order", query.order);
  }
  if (query.search) params.set("q", query.search);

  const queryString = params.toString();

  return `${window.location.pathname}${queryString ? `?${queryString}` : ""}`;
}

type CustomersListProps = {
  /**
   * data-calling on the mount point: "true" when show_customer1.php decided
   * (CallAccess) this user works the phones. Accounting gets no softphone.
   */
  calling?: string;
};

export default function CustomersList({ calling }: CustomersListProps) {
  const [query, setQuery] = useState<ListQuery>(() =>
    readQuery(window.location.search),
  );
  const [searchInput, setSearchInput] = useState<string>(query.search);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [meta, setMeta] = useState<CustomersMeta>(EMPTY_META);
  const [permissions, setPermissions] =
    useState<CustomerPermissions>(NO_PERMISSIONS);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const requestId = useRef<number>(0);

  /**
   * push adds a history entry (paging, sorting); replace does not (the page
   * correcting itself), so Back never lands on a page that cannot exist.
   */
  const navigate = useCallback(
    (changes: Partial<ListQuery>, mode: "push" | "replace" = "push") => {
      const next = { ...query, ...changes };
      const url = writeQuery(next);

      // A double click or a repeated search asks for the view already on
      // screen: no second request, no duplicate Back step.
      const unchanged = (Object.keys(next) as (keyof ListQuery)[]).every(
        (key) => next[key] === query[key],
      );
      if (unchanged) return;

      if (mode === "push") window.history.pushState(null, "", url);
      else window.history.replaceState(null, "", url);

      setQuery(next);
    },
    [query],
  );

  useEffect(() => {
    const restore = () => {
      const restored = readQuery(window.location.search);
      setQuery(restored);
      setSearchInput(restored.search);
    };

    window.addEventListener("popstate", restore);

    return () => window.removeEventListener("popstate", restore);
  }, []);

  useEffect(() => {
    // Sorting and paging fire overlapping requests; only the newest may render.
    requestId.current += 1;
    const thisRequest = requestId.current;
    const isStale = () => thisRequest !== requestId.current;

    const load = async () => {
      setLoading(true);
      setError("");
      setSessionExpired(false);

      try {
        const response = await CustomersService().list({
          page: query.page,
          perPage: query.perPage,
          sort: query.sort,
          order: query.order,
          search: query.search,
        });

        if (isStale()) return;

        if (response.code !== 200) {
          // 401: the token expired under a live session; a reload re-issues it.
          if (response.code === 401) setSessionExpired(true);
          setError(response.code === 401 ? SESSION_EXPIRED : LOAD_ERROR);
          setCustomers([]);
          setMeta(EMPTY_META);
          return;
        }

        const responseMeta = (response.meta as CustomersMeta) || EMPTY_META;

        // A bookmarked page past the end (customers were deleted, or the
        // search narrowed) goes to the last real page instead of "empty".
        if (
          responseMeta.total_pages > 0 &&
          query.page > responseMeta.total_pages
        ) {
          // Retire this request first, so its `finally` does not flash the
          // empty state before the corrected request starts.
          requestId.current += 1;
          navigate({ page: responseMeta.total_pages }, "replace");
          return;
        }

        setCustomers((response.data as Customer[]) || []);
        setMeta(responseMeta);
        setPermissions(responseMeta.permissions || NO_PERMISSIONS);
      } catch {
        if (isStale()) return;

        setError(LOAD_ERROR);
        setCustomers([]);
        setMeta(EMPTY_META);
      } finally {
        if (!isStale()) setLoading(false);
      }
    };

    load();
  }, [navigate, query]);

  const changeSort = (sort: CustomerSort) => {
    if (sort === query.sort) {
      navigate({ page: 1, order: query.order === "asc" ? "desc" : "asc" });
      return;
    }

    navigate({
      page: 1,
      sort,
      order: defaultOrder(sort),
    });
  };

  const applySearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    navigate({ page: 1, search: searchInput.trim() });
  };

  const firstShown = (meta.page - 1) * meta.per_page + 1;
  const lastShown = Math.min(meta.page * meta.per_page, meta.total);

  const pagination = (label: string) => (
    <Pagination
      page={meta.page}
      totalPages={meta.total_pages}
      onPageChange={(page) => navigate({ page })}
      tone="app"
      numbered
      label={label}
      previousLabel="Atrás"
      nextLabel="Siguiente"
    />
  );

  return (
    <AppLayout
      title="Pacientes Registrados"
      description="Busca, ordena y abre a cualquiera de tus pacientes."
    >
      {calling === "true" ? <CustomerCalling embedded /> : null}

      {error ? (
        // ring, not border: Bootstrap's !important .border would gray it out.
        <div
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200 dark:bg-red-950 dark:text-red-300 dark:ring-red-900"
          role="alert"
        >
          <span>{error}</span>
          {sessionExpired ? (
            <button
              type="button"
              className="app-control rounded-md px-3 py-1.5 text-sm"
              onClick={() => window.location.reload()}
            >
              Recargar
            </button>
          ) : null}
        </div>
      ) : null}

      <section className={cardClasses} aria-label="Pacientes">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 p-4 dark:border-slate-700 sm:p-6">
          <form
            role="search"
            className="flex flex-wrap items-end gap-2"
            onSubmit={applySearch}
          >
            <div>
              <label className={labelClasses} htmlFor="customers-search">
                Buscar
              </label>
              <input
                id="customers-search"
                type="search"
                value={searchInput}
                placeholder="Nombre, apellido, email o celular"
                onChange={(event) => setSearchInput(event.target.value)}
                className={`${inputClasses} w-72`}
              />
            </div>
            <Button type="submit">Buscar</Button>
          </form>

          <div className="flex items-end gap-4">
            {/* Always in the DOM: a live region that appears already filled
                is often not announced. */}
            <p
              className="pb-2.5 text-sm text-gray-600 dark:text-gray-400"
              aria-live="polite"
            >
              {meta.total > 0
                ? `Mostrando ${firstShown}–${lastShown} de ${meta.total}`
                : ""}
            </p>

            <div>
              <label className={labelClasses} htmlFor="customers-per-page">
                Por página
              </label>
              <select
                id="customers-per-page"
                value={query.perPage}
                onChange={(event) =>
                  navigate({ page: 1, perPage: Number(event.target.value) })
                }
                className={inputClasses}
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {meta.total_pages > 1 ? (
          <div className="border-b border-gray-200 px-4 pb-4 dark:border-slate-700 sm:px-6">
            {pagination("Paginación superior")}
          </div>
        ) : null}

        {/* Under an error the empty table would claim there are no
            patients; the alert above says what actually happened. */}
        {error ? null : (
          <CustomersTable
            customers={customers}
            loading={loading}
            permissions={permissions}
            sort={query.sort}
            order={query.order}
            onSort={changeSort}
            emptyMessage={
              query.search
                ? `Ningún paciente coincide con «${query.search}».`
                : undefined
            }
          />
        )}

        {meta.total_pages > 1 ? (
          <div className="border-t border-gray-200 px-4 pb-4 dark:border-slate-700 sm:px-6">
            {pagination("Paginación inferior")}
          </div>
        ) : null}
      </section>
    </AppLayout>
  );
}
